import React, { useMemo, useState } from "react";
import { PreLoading } from "@tripian/react";

export type LivnPaymentLine = {
  title: string;
  quantity: number;
  amount: number;
  currency: string;
};

interface ILivnPaymentStep {
  productName: string;
  travelDate?: string;
  timeLabel?: string;
  paxCount: number;
  lines: LivnPaymentLine[];
  total: number;
  currency: string;
  cancellationPolicy?: string;
  generalTerms?: string;
  localFees?: string;
  submitting: boolean;
  error?: string;
  onBack: () => void;
  onPay: () => void;
}

type CardForm = {
  cardholderName: string;
  cardNumber: string;
  cardType: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
};

const CARD_TYPES = [
  { value: "VI", label: "Visa" },
  { value: "MC", label: "Mastercard" },
  { value: "AX", label: "American Express" },
];

const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

const emptyCard = (currentYear: number): CardForm => ({
  cardholderName: "",
  cardNumber: "",
  cardType: "VI",
  expiryMonth: "01",
  expiryYear: String(currentYear),
  cvv: "",
});

/** Luhn checksum — catches transposed/mistyped digits that a length check alone lets through. */
const passesLuhn = (digits: string): boolean => {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return digits.length > 0 && sum % 10 === 0;
};

const formatCardNumber = (raw: string): string =>
  raw
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const LivnPaymentStep: React.FC<ILivnPaymentStep> = ({
  productName,
  travelDate,
  timeLabel,
  paxCount,
  lines,
  total,
  currency,
  cancellationPolicy,
  generalTerms,
  localFees,
  submitting,
  error,
  onBack,
  onPay,
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const years = useMemo(() => Array.from({ length: 15 }, (_, i) => String(currentYear + i)), [currentYear]);

  const [card, setCard] = useState<CardForm>(() => emptyCard(currentYear));
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const errors = useMemo(() => {
    const e: Partial<Record<keyof CardForm | "terms", string>> = {};
    if (!card.cardholderName.trim()) e.cardholderName = "Cardholder name is required.";
    else if (!/^[A-Za-z][A-Za-z\s'.-]{1,}$/.test(card.cardholderName.trim()))
      e.cardholderName = "Please enter the name as printed on the card.";

    const digits = card.cardNumber.replace(/\D/g, "");
    if (!digits) e.cardNumber = "Card number is required.";
    else if (digits.length < 13 || digits.length > 19) e.cardNumber = "Card number must be 13–19 digits.";
    else if (!passesLuhn(digits)) e.cardNumber = "Please check the card number.";

    const cvvLength = card.cardType === "AX" ? 4 : 3;
    if (!card.cvv) e.cvv = "Security code is required.";
    else if (!new RegExp(`^\\d{${cvvLength}}$`).test(card.cvv)) e.cvv = `Security code must be ${cvvLength} digits.`;

    const year = Number(card.expiryYear);
    const month = Number(card.expiryMonth);
    if (year === currentYear && month < currentMonth) e.expiryMonth = "This card has expired.";

    if (!termsAccepted) e.terms = "Please accept the booking terms to continue.";
    return e;
  }, [card, termsAccepted, currentYear, currentMonth]);

  const isValid = Object.keys(errors).length === 0;

  const set = (k: keyof CardForm, v: string) => setCard(prev => ({ ...prev, [k]: v }));

  const handlePay = () => {
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    onPay();
  };

  const err = (k: keyof CardForm | "terms") => (showErrors ? errors[k] : undefined);
  const fieldCls = (k: keyof CardForm) =>
    `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary-color ${
      err(k) ? "border-red-400 bg-red-50/40" : "border-gray-300"
    }`;

  // The quote can be missing if Livn has not returned line items for this step yet — never
  // show or authorise a "0" charge; fall back to wording that makes the state honest.
  const hasAmount = total > 0;
  const amount = hasAmount ? `${Math.round(total * 100) / 100} ${currency}` : "the amount confirmed by the supplier";

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Review &amp; Payment</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Your booking is not confirmed yet. Review the details below and complete payment to confirm.
        </p>
      </div>

      {/* ── Order summary ── */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-sm font-semibold text-gray-800">{productName}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {[travelDate, timeLabel, `${paxCount} ${paxCount === 1 ? "traveller" : "travellers"}`]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <div className="border-t border-gray-200 mt-3 pt-3 space-y-1">
          {lines.map((line, i) => (
            <div key={i} className="flex justify-between text-sm text-gray-600">
              <span>
                {line.title} × {line.quantity}
              </span>
              <span>
                {line.amount} {line.currency}
              </span>
            </div>
          ))}
          <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-semibold text-sm text-gray-800">
            <span>Amount due today</span>
            <span>{hasAmount ? `${Math.round(total * 100) / 100} ${currency}` : "To be confirmed"}</span>
          </div>
        </div>
      </div>

      {localFees && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <span className="font-medium">Payable locally: </span>
          {localFees}
        </div>
      )}

      {/* ── Card details ── */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800">Payment Details</h3>
          <span className="text-xs text-gray-400">🔒 Secure &amp; encrypted</span>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Cardholder Name<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              className={fieldCls("cardholderName")}
              value={card.cardholderName}
              placeholder="As printed on the card"
              autoComplete="cc-name"
              onChange={e => set("cardholderName", e.target.value)}
            />
            {err("cardholderName") && <p className="text-xs text-red-500 mt-1">{err("cardholderName")}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">
                Card Number<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                className={fieldCls("cardNumber")}
                value={card.cardNumber}
                inputMode="numeric"
                autoComplete="cc-number"
                placeholder="0000 0000 0000 0000"
                onChange={e => set("cardNumber", formatCardNumber(e.target.value))}
              />
              {err("cardNumber") && <p className="text-xs text-red-500 mt-1">{err("cardNumber")}</p>}
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Card Type</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary-color"
                value={card.cardType}
                onChange={e => set("cardType", e.target.value)}
              >
                {CARD_TYPES.map(ct => (
                  <option key={ct.value} value={ct.value}>
                    {ct.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Expiry Month</label>
              <select
                className={fieldCls("expiryMonth")}
                value={card.expiryMonth}
                autoComplete="cc-exp-month"
                onChange={e => set("expiryMonth", e.target.value)}
              >
                {MONTHS.map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Expiry Year</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary-color"
                value={card.expiryYear}
                autoComplete="cc-exp-year"
                onChange={e => set("expiryYear", e.target.value)}
              >
                {years.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                CVV<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                className={fieldCls("cvv")}
                value={card.cvv}
                inputMode="numeric"
                autoComplete="cc-csc"
                placeholder={card.cardType === "AX" ? "0000" : "000"}
                maxLength={card.cardType === "AX" ? 4 : 3}
                onChange={e => set("cvv", e.target.value.replace(/\D/g, ""))}
              />
              {err("cvv") && <p className="text-xs text-red-500 mt-1">{err("cvv")}</p>}
            </div>
          </div>
          {err("expiryMonth") && <p className="text-xs text-red-500">{err("expiryMonth")}</p>}
        </div>
      </div>

      {/* ── Policies ── */}
      {(cancellationPolicy || generalTerms) && (
        <div className="space-y-2">
          {cancellationPolicy && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
              <p className="font-medium mb-1">Cancellation Policy</p>
              <p>{cancellationPolicy}</p>
            </div>
          )}
          {generalTerms && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
              <p className="font-medium mb-1">Terms &amp; Conditions</p>
              <p>{generalTerms}</p>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={termsAccepted}
            onChange={e => setTermsAccepted(e.target.checked)}
          />
          <span>
            I authorise a charge of <strong>{amount}</strong> and accept the cancellation policy and terms above.
          </span>
        </label>
        {err("terms") && <p className="text-xs text-red-500 mt-1">{err("terms")}</p>}
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="flex gap-3">
        <button
          className="px-4 py-3 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          onClick={onBack}
          disabled={submitting}
        >
          ← Back
        </button>
        <button
          className="flex-1 py-3 rounded-lg bg-primary-color text-white font-medium hover:bg-primary-color/90 transition-colors disabled:opacity-50"
          onClick={handlePay}
          disabled={submitting}
        >
          {submitting ? (
            <PreLoading size="small" color="white" />
          ) : hasAmount ? (
            `Pay ${amount} & Confirm Booking`
          ) : (
            "Confirm Booking"
          )}
        </button>
      </div>
    </div>
  );
};

export default LivnPaymentStep;
